/**
 * PatientMembershipsPanel.tsx
 *
 * Patient-detail memberships tab.
 * Card-based layout with Active / Canceled sections, fixed header,
 * matching the appointments tab pattern.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import {
  getPatientRecurringProfiles,
  type RecurringPaymentRow,
} from '@/services/memberships.service';
import { getErrorMessage } from '@/services/apiClient';
import { todayYmd } from '@/lib/utils';
import { patientQueryKeys } from '@/hooks/usePatientDetail';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui';
import {
  MembershipBillingDates,
  MembershipCancelsDate,
  formatShortDate,
  isKeepThroughPending,
} from '@/components/memberships/MembershipCardDates';
import { AddMembershipModal } from './AddMembershipModal';
import { RecurringDetailModal } from './RecurringDetailModal';

/* ── Helpers ──────────────────────────────────────────────────────── */

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function accentColor(profile: RecurringPaymentRow, isCancelling = false): string {
  if (isCancelling) return '#ef4444';
  if (profile.incrementType === '0') return '#94a3b8';
  if (profile.paymentStatusDisplay === 'Declined') return '#ef4444';
  switch (profile.status) {
    case 'Active': return '#22c55e';
    case 'Frozen': return '#3b82f6';
    default: return '#f59e0b';
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'Active':
      return <Badge variant="success" size="sm">Active</Badge>;
    case 'Frozen':
      return <Badge variant="info" size="sm">Frozen</Badge>;
    case 'Inactive':
      return <Badge variant="neutral" size="sm">Inactive</Badge>;
    default:
      return <Badge variant="neutral" size="sm">{status}</Badge>;
  }
}

/* ── Card ─────────────────────────────────────────────────────────── */

function isCancellingProfile(profile: RecurringPaymentRow, todayStr: string): boolean {
  // Cancelled with "keep": it bills no more and ends when its credits run out.
  if (isKeepThroughPending(profile.prepay)) return true;
  if (profile.incrementType !== '0') return false;
  const throughYmd = (profile.paymentThroughDate ?? '').slice(0, 10);
  return throughYmd >= todayStr;
}

function MembershipCard({
  profile,
  onClick,
  section,
  cancelling,
}: {
  profile: RecurringPaymentRow;
  onClick: () => void;
  section: 'upcoming' | 'active' | 'canceled';
  cancelling?: boolean;
}) {
  const border = accentColor(profile, cancelling);
  const isDeclined = profile.paymentStatusDisplay === 'Declined';
  const isCanceled = section === 'canceled';

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex w-full gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300 hover:shadow"
      style={{ borderLeftWidth: 4, borderLeftColor: border }}
    >
      {/* Status badges — top right */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        {isDeclined && <Badge variant="error" size="sm">Declined</Badge>}
        {cancelling
          ? <Badge variant="error" size="sm">Set to Cancel</Badge>
          : section === 'upcoming'
            ? <Badge variant="warning" size="sm">Upcoming</Badge>
            : statusBadge(isCanceled ? 'Inactive' : profile.status)}
      </div>

      {/* Card body */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <p className="text-sm font-semibold text-slate-800 truncate pr-28">
          {profile.membershipTitle || 'Membership'}
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-bold text-slate-900">
            {formatCurrency(profile.curAmount)}
          </span>
          {profile.strPayMethod && (
            <span className="text-xs text-slate-500">{profile.strPayMethod}</span>
          )}
        </div>

        <div className="text-xs text-slate-500 space-y-0.5">
          <div>
            Start Date: <span className="font-medium text-slate-700">{profile.firstBillingDate ? formatShortDate(profile.firstBillingDate) : '—'}</span>
          </div>

          {cancelling && (
            <MembershipCancelsDate
              date={profile.reactivationThroughDate ?? profile.paymentThroughDate}
              prepay={profile.prepay}
            />
          )}

          {section === 'active' && !cancelling && (
            <MembershipBillingDates
              nextBillingDate={profile.nextBillingDate}
              paymentThroughDate={profile.paymentThroughDate}
              prepay={profile.prepay}
            />
          )}

          {section === 'canceled' && (
            <div>
              Cancel Date: <span className="font-medium text-slate-700">{profile.endDate ? formatShortDate(profile.endDate) : '—'}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

/* ── Panel ────────────────────────────────────────────────────────── */

interface PatientMembershipsPanelProps {
  patientId: number;
}

export function PatientMembershipsPanel({ patientId }: PatientMembershipsPanelProps) {
  const queryClient = useQueryClient();
  const [profiles, setProfiles] = useState<RecurringPaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<RecurringPaymentRow | null>(null);
  const [canceledExpanded, setCanceledExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const profileData = await getPatientRecurringProfiles(patientId);
      setProfiles(profileData);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { void load(); }, [load]);

  const todayStr = todayYmd();

  const upcoming = useMemo(
    () => profiles
      .filter((p) => {
        if (p.incrementType === '0') return false;
        const first = (p.firstBillingDate ?? '').slice(0, 10);
        return first > todayStr;
      })
      .sort((a, b) => (a.firstBillingDate ?? '').localeCompare(b.firstBillingDate ?? '')),
    [profiles, todayStr],
  );

  const active = useMemo(
    () => profiles
      .filter((p) => {
        if (p.incrementType === '0') return isCancellingProfile(p, todayStr);
        const first = (p.firstBillingDate ?? '').slice(0, 10);
        return first <= todayStr || !first;
      })
      .sort((a, b) => (a.nextBillingDate ?? '').localeCompare(b.nextBillingDate ?? '')),
    [profiles, todayStr],
  );

  const canceled = useMemo(
    () => profiles
      .filter((p) => p.incrementType === '0' && !isCancellingProfile(p, todayStr))
      .sort((a, b) => b.createDate.localeCompare(a.createDate)),
    [profiles, todayStr],
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Fixed header */}
      <div className="flex-shrink-0 pb-4">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-slate-900">Memberships</h2>
          <Button
            onClick={() => setEnrollModalOpen(true)}
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Membership
          </Button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-6">
        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-400">Loading memberships…</p>
        ) : profiles.length === 0 ? (
          <p className="py-4 text-sm text-slate-400 italic">No memberships found.</p>
        ) : (
          <>
            {/* Upcoming */}
            {upcoming.length > 0 && (
              <section>
                <h3 className="text-sm font-bold text-amber-600 border-b border-slate-200 pb-2 mb-3">
                  Upcoming ({upcoming.length})
                </h3>
                <div className="space-y-3">
                  {upcoming.map((p) => (
                    <MembershipCard
                      key={p.paymentsRecurringAuthnetID}
                      profile={p}
                      section="upcoming"
                      onClick={() => setSelectedProfile(p)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Active */}
            <section>
              <h3 className="text-sm font-bold text-slate-700 border-b border-slate-200 pb-2 mb-3">
                Active ({active.length})
              </h3>
              {active.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No active memberships.</p>
              ) : (
                <div className="space-y-3">
                  {active.map((p) => (
                    <MembershipCard
                      key={p.paymentsRecurringAuthnetID}
                      profile={p}
                      section="active"
                      cancelling={isCancellingProfile(p, todayStr)}
                      onClick={() => setSelectedProfile(p)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Canceled (collapsible) */}
            {canceled.length > 0 && (
              <section>
                <button
                  type="button"
                  onClick={() => setCanceledExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 text-sm font-bold text-slate-500 border-b border-slate-200 pb-2 mb-3 cursor-pointer hover:text-slate-700 transition-colors"
                >
                  {canceledExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Canceled ({canceled.length})
                </button>
                {canceledExpanded && (
                  <div className="space-y-3">
                    {canceled.map((p) => (
                      <MembershipCard
                        key={p.paymentsRecurringAuthnetID}
                        profile={p}
                        section="canceled"
                        onClick={() => setSelectedProfile(p)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>

      <AddMembershipModal
        patientId={patientId}
        open={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
        onSaved={() => { void load(); void queryClient.invalidateQueries({ queryKey: patientQueryKeys.memberships(String(patientId)) }); }}
      />

      <RecurringDetailModal
        recurringId={selectedProfile?.paymentsRecurringAuthnetID ?? null}
        open={!!selectedProfile}
        onClose={() => setSelectedProfile(null)}
        onChanged={() => { void load(); void queryClient.invalidateQueries({ queryKey: patientQueryKeys.memberships(String(patientId)) }); }}
      />
    </div>
  );
}
